import Point from '@arcgis/core/geometry/Point';
import * as webMercatorUtils from '@arcgis/core/geometry/support/webMercatorUtils';
import { DEFAULT_MAP_SOURCE } from '@env';
import { isArray } from 'lodash';
import { Subject } from 'rxjs';

import { Injectable } from '@angular/core';

import { FsMapControlsModes, ShapeStylesType } from '@lib/geo/helpers';
import { FsDrawingToolsType, FsMap } from '@lib/geo/map/types';

import LatLngLiteral = google.maps.LatLngLiteral;

@Injectable({
  providedIn: 'root',
})
export class FsMapControlsService {
  get activeDrawingTool() {
    return this.activeTool;
  }

  get defaultMapSource() {
    return DEFAULT_MAP_SOURCE;
  }

  public activeTool: FsDrawingToolsType;

  private readonly _destroy$: Subject<void> = new Subject();

  constructor() {}

  public zoomIn(map: FsMap): void {
    map.setZoom(map.getZoom() + 1);
  }

  public zoomOut(map: FsMap): void {
    map.setZoom(map.getZoom() - 1);
  }

  public setStyle(map: FsMap, style: string): void {
    map.setStyle(style);
  }

  public drawCircle(data: {
    map: FsMap;
    mode?: FsMapControlsModes;
    style?: ShapeStylesType;
  }): void {
    const { map, mode, style } = data;
    this.activeTool = FsDrawingToolsType.CIRCLE;
    map.emitDrawing(FsDrawingToolsType.CIRCLE, mode, style);
  }

  public drawFreehand(map: FsMap): void {
    this.activeTool = FsDrawingToolsType.PEN;
    map.freehand();
  }

  public drawPoint(data: { map: FsMap; mode?: FsMapControlsModes; style?: ShapeStylesType }): void {
    const { map, mode, style } = data;
    this.activeTool = FsDrawingToolsType.POINT;
    map.emitDrawing(FsDrawingToolsType.POINT, mode, style);
  }

  public drawPolygon(data: {
    map: FsMap;
    mode?: FsMapControlsModes;
    style?: ShapeStylesType;
  }): void {
    const { map, mode, style } = data;
    this.activeTool = FsDrawingToolsType.POLYGON;
    map.emitDrawing(FsDrawingToolsType.POLYGON, mode, style);
  }

  public drawPolyline(data: {
    map: FsMap;
    mode?: FsMapControlsModes;
    style?: ShapeStylesType;
  }): void {
    const { map, mode, style } = data;
    this.activeTool = FsDrawingToolsType.POLYLINE;
    map.emitDrawing(FsDrawingToolsType.POLYLINE, mode, style);
  }

  public drawRectangle(data: {
    map: FsMap;
    mode?: FsMapControlsModes;
    style?: ShapeStylesType;
  }): void {
    const { map, mode, style } = data;
    this.activeTool = FsDrawingToolsType.RECTANGLE;
    map.emitDrawing(FsDrawingToolsType.RECTANGLE, mode, style);
  }

  public setActiveDrawingTool(tool: FsDrawingToolsType) {
    this.activeTool = tool;
  }

  public saveShapeData(
    shape: any,
    processGeoJsonFeature: (featureCollection: any, close?: boolean) => void,
    close?: boolean,
  ): void {
    const activeTool = this.activeDrawingTool;
    shape = shape.shape || shape;
    switch (this.defaultMapSource) {
      case 'google':
        const dataLayer = new google.maps.Data();
        switch (activeTool) {
          case FsDrawingToolsType.PEN:
          case FsDrawingToolsType.POLYGON:
            const path = shape.getPath();
            const paths: LatLngLiteral[] = (isArray(path) ? path : path.getArray()).map((v) => {
              return {
                lat: v[1] ?? v.lat(),
                lng: v[0] ?? v.lng(),
              };
            });
            dataLayer.add(
              new google.maps.Data.Feature({
                geometry: new google.maps.Data.MultiPolygon([[paths]]),
                properties: {
                  type: 'polygon',
                },
              }),
            );
        }
        dataLayer.toGeoJson((featureCollection: any) => {
          processGeoJsonFeature(featureCollection, close);
        });
        break;
      case 'esri':
        const shapeCoords = shape.geometry.paths || shape.geometry.rings;
        const rings = !shape.geometry.spatialReference.isWebMercator
          ? shapeCoords.map((ring) => ring.map((coord) => [coord[0], coord[1]]))
          : shapeCoords.map((ring) =>
              ring.map((coord) => {
                const point = new Point({
                  x: coord[0],
                  y: coord[1],
                  spatialReference: { wkid: shape.geometry.spatialReference.wkid },
                });

                const geographicPoint = webMercatorUtils.webMercatorToGeographic(point) as Point;
                return [geographicPoint.x, geographicPoint.y]; // Return in [lng, lat]
              }),
            );
        const geoJson = {
          type: 'polygon',
          coordinates: [rings],
        };
        processGeoJsonFeature(
          { features: [{ geometry: geoJson, properties: { type: 'polygon' } }] },
          close,
        );
    }
  }

  public cleanup() {
    this._destroy$.next();
    this._destroy$.complete();
  }
}
